import React, { ChangeEvent } from "react";

import config from "../../../../config/config";
import { ModalContext } from "../../../modal/modal";
import FileBrowser from "../../../file-browser/file-browser";

const fileIcon = <svg width="100%" height="auto" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path d="M0 64C0 28.7 28.7 0 64 0L224 0l0 128c0 17.7 14.3 32 32 32l128 0 0 288c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zm384 64l-128 0L256 0 384 128z"/></svg>;

const defaultAcceptedMediaExtensions = {
  font: ['.woff2', '.woff', '.ttf', '.otf'],
  image: ['.gif', '.jpg', '.jpeg', '.png', '.heic'],
  video: ['.mov', '.mp4', '.mpeg', '.webm', '.ogg'],
  audio: ['.mp3', '.wav', '.ogg'],
  document: ['.pdf', '.docx', '.txt', '.xlsx', '.pptx'],
  style: ['.css'],
  archive: ['.zip', '.rar', '.tar', '.gz'],
  code: ['.js', '.ts', '.jsx', '.tsx', '.html', '.css', '.scss', '.json'],
  other: ['.md', '.yaml', '.xml', '.csv', '.log']
};

interface FileBrowserInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  // init?: { [key: string]: string},
  init?: string,
  acceptedMediaExtensions?: { [key: string]: string[] },
  sources?: { 
    [key: string]: {
      readDirectoryRoute: string;
      createDirectoryRoute?: string;
      deleteMediaRoute?: string;
      uploadMediaRoute?: string;
    }
  },
  fallback?: string | JSX.Element // not implemented yet
}

const determineFileTypeByExtension = (filename: string, acceptedMediaExtensions: { [key: string]: string[] }) => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  for (const [type, extensions] of Object.entries(acceptedMediaExtensions)) {
    if (extensions.map(e => e.toLowerCase()).includes(`.${ext}`)) {
      return type;
    }
  }
  return 'other';
}

const FileBrowserModal: React.FC<{ 
  resolve: (value: { filename: string } | null) => any, 
  acceptedMediaExtensions?: { [key: string]: string[]} 
  sources: { [key: string]: {
    readDirectoryRoute: string;
    createDirectoryRoute?: string;
    deleteMediaRoute?: string;
    uploadMediaRoute?: string;
  } }
}> = ({ 
  resolve, 
  acceptedMediaExtensions = defaultAcceptedMediaExtensions,
  sources = {
    'Disc': {
      readDirectoryRoute: 'getdirectory',
      createDirectoryRoute: 'createdirectory',
      deleteMediaRoute: 'deletemedia',
      uploadMediaRoute: 'uploadmedia'
    }
  }
}) => {

  return (
    <FileBrowser
      acceptedMediaExtensions={acceptedMediaExtensions}
      sources={sources}
      onPick={(file) => {
        if (file) {
          resolve({ filename: file });
        } else {
          // resolve(null);
        }
      }}
    />
  );

}

const FileBrowserInput: React.FC<FileBrowserInputProps> = ({ 
  acceptedMediaExtensions = defaultAcceptedMediaExtensions,
  sources = {
    'Disc': {
      readDirectoryRoute: 'getdirectory',
      createDirectoryRoute: 'createdirectory',
      deleteMediaRoute: 'deletemedia',
      uploadMediaRoute: 'uploadmedia'
    }
  },
  fallback = fileIcon,
  value, name, placeholder, onChange, init, className, ...rest}) => {

  // const [selectedValue, setSelectedValue] = React.useState<string>(init?.[Object.keys(init)[0] as keyof typeof init] || ''); // how does this work?
  const [selectedValue, setSelectedValue] = React.useState<string>(init || '');
  const [selectedFileType, setSelectedFileType] = React.useState<string>(determineFileTypeByExtension(selectedValue, acceptedMediaExtensions));

  const inputRef = React.useRef<HTMLInputElement>(null);
  const modalContext = React.useContext(ModalContext);

  React.useEffect(() => {
    setSelectedFileType(determineFileTypeByExtension(selectedValue, acceptedMediaExtensions));
  }, []);

  React.useEffect(() => {
    setSelectedFileType(determineFileTypeByExtension(selectedValue, acceptedMediaExtensions));
    if (onChange && inputRef.current) {
      inputRef.current.value = selectedValue;
      const event = {
        target: {
          value: selectedValue
        }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
      // console.log('FileBrowserInput useEffect onChange', event);
    }
  }, [selectedValue]);

  const openFileBrowser = async () => {
    const modalRes = await (new Promise<{ filename: string} | null>((res, rej) => modalContext.modal!({
      node: <FileBrowserModal resolve={res} acceptedMediaExtensions={acceptedMediaExtensions || defaultAcceptedMediaExtensions} sources={sources} />,
      resolve: res , reject: rej
    })));
    modalContext.modal!();
    if (modalRes && modalRes.filename) {
      // console.log(modalRes.filename.replace(config.ASSETS[config.ENVIRONMENT], ''));
      setSelectedValue(modalRes.filename.replace(config.ASSETS[config.ENVIRONMENT], ''));                
    }
  }

  return (
    <div>
      <div className="relative w-full h-0 pt-[100%] rounded-md bg-blue-400 overflow-hidden">
        {
          !!(selectedValue.length) &&
          (() => {
            switch (selectedFileType) {
              case 'image':
                return (
                  <img
                    loading={"lazy"}
                    className="absolute top-0 left-0 right-0 bottom-0 m-0 p-0 w-full h-full object-cover rounded-md hover:cursor-pointer"
                    onClick={openFileBrowser} 
                    src={
                      (
                        selectedValue.toUpperCase().startsWith('HTTP://') ||
                        selectedValue.toUpperCase().startsWith('HTTPS://') ||
                        selectedValue.toUpperCase().startsWith('WWW.')
                      ) ?
                        selectedValue
                      :
                        config.ASSETS[config.ENVIRONMENT] + selectedValue
                    }
                  >
                  </img>
                );
              case 'video':
                return (
                  <video
                    playsInline webkit-playsInline webkit-playsinline controlsList="nodownload"
                    className="absolute top-0 left-0 right-0 bottom-0 m-0 p-0 w-full h-full object-cover rounded-md hover:cursor-pointer"
                    onClick={openFileBrowser} 
                    src={
                      (
                        selectedValue.toUpperCase().startsWith('HTTP://') ||
                        selectedValue.toUpperCase().startsWith('HTTPS://') ||
                        selectedValue.toUpperCase().startsWith('www.')
                      ) ?
                        selectedValue
                      :
                        config.ASSETS[config.ENVIRONMENT] + selectedValue
                    }
                    controls={false} autoPlay={false} muted={true} loop={true}
                  ></video>
                );
              case 'audio':
                return (
                  <div className="absolute top-0 left-0 right-0 bottom-0 m-0 p-0 w-full h-full flex items-center justify-center rounded-md bg-black hover:cursor-pointer" onClick={openFileBrowser}>
                    <audio
                      className="w-full h-16"
                      src={
                        (
                          selectedValue.toUpperCase().startsWith('HTTP://') ||
                          selectedValue.toUpperCase().startsWith('HTTPS://') ||
                          selectedValue.toUpperCase().startsWith('www.')
                        ) ?
                          selectedValue
                        :
                          config.ASSETS[config.ENVIRONMENT] + selectedValue
                      }
                      controls={true} autoPlay={false} muted={false} loop={false}
                    ></audio>
                  </div>
                );
              default:
                return <div className="absolute top-0 left-0 right-0 bottom-0 m-0 w-full h-full object-cover rounded-md hover:cursor-pointer p-1 text-indigo-400">{fileIcon}</div>;
            }
          })()
        }
        {
          !(selectedValue.length) &&
            <svg className="absolute top-0 left-0 right-0 bottom-0 m-0 p-0 w-full h-full object-cover rounded-md hover:cursor-pointer" onClick={openFileBrowser} xmlns="http://www.w3.org/2000/svg" width="100%" viewBox="0 0 512 512"><path d="M448 80c8.8 0 16 7.2 16 16V415.8l-5-6.5-136-176c-4.5-5.9-11.6-9.3-19-9.3s-14.4 3.4-19 9.3L202 340.7l-30.5-42.7C167 291.7 159.8 288 152 288s-15 3.7-19.5 10.1l-80 112L48 416.3l0-.3V96c0-8.8 7.2-16 16-16H448zM64 32C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V96c0-35.3-28.7-64-64-64H64zm80 192a48 48 0 1 0 0-96 48 48 0 1 0 0 96z"/></svg>
        }

      </div>

      <input
        ref={inputRef}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setSelectedValue(e.target.value)}}
        className={`hidden`}
        placeholder={placeholder}
        {...rest}
      />
    </div>
  );
};

export default FileBrowserInput ;